import { useState, useCallback } from 'react';
import { checkExtensionInstalled } from '@/utils/extensionDiagnostics';
import { useAuthStore } from '@/stores/authStore';

const BETA_EXTENSION_ID = 'mhihojgkhappoepbgbbbknlooldcplfi';
const BETA_EXTENSION_URL = 'https://chromewebstore.google.com/detail/notego-screen-recorder-be/' + BETA_EXTENSION_ID;

// The beta extension is published against production only, so it can't talk to
// staging (notegostaging.firebaseapp.com) or local. Everywhere but production
// uses the environment's own extension (VITE_CHROME_EXTENSION_ID /
// VITE_CHROME_EXTENSION_URL) — the only build that works there. Both builds run
// `vite build --mode production`, so import.meta.env can't tell them apart; the
// host is the only reliable signal.
const PRODUCTION_HOST = 'app.notego.ai';

const isProductionHost = () => window.location.hostname === PRODUCTION_HOST;

export const useExtensionManager = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { userData } = useAuthStore();

  const isBetaTester = isProductionHost() && userData?.subscription?.product_name === 'BETA TESTER';

  // NOTE: Plan features (including isTeamMember) are sent to the extension by
  // usePlanFeatureSync — the single, app-wide source of truth mounted once in
  // ProtectedRoutes. This hook owns ONLY extension install detection + the install
  // modal. Do NOT re-add a sendPlanFeaturesToExtension call here: this hook is
  // mounted in several components, so doing so re-derives isTeamMember and races
  // the same UPDATE_PLAN_FEATURES message from multiple places.
  const isExtensionInstalled = useCallback(async () => {
    return checkExtensionInstalled(undefined, isBetaTester ? BETA_EXTENSION_ID : undefined);
  }, [isBetaTester]);

  const openExtension = async () => {
    window.postMessage({ type: 'OPEN_EXTENSION' }, '*');
  };

  const checkExtension = async () => {
    const isInstalled = await isExtensionInstalled();
    if (isInstalled) {
      openExtension();
    } else {
      setIsModalOpen(true);
    }
  };

  const onModalConfirm = () => {
    const url = isBetaTester ? BETA_EXTENSION_URL : import.meta.env.VITE_CHROME_EXTENSION_URL;
    if (!url) {
      setIsModalOpen(false);
      return;
    }
    window.open(url, '_blank');
    setIsModalOpen(false);
  };

  return {
    isModalOpen,
    setIsModalOpen,
    checkExtensionInstalled: checkExtension,
    onModalConfirm,
    isExtensionInstalled,
  };
};
